/**
 * Effect system: each effect is a declarative definition (params + a build
 * function that wires Web Audio nodes). An EffectInstance wraps the live
 * nodes; EffectChain rebuilds the chain when slots change and patches params
 * in place otherwise.
 */

export interface ParamDef {
  key: string;
  label: string;
  min: number;
  max: number;
  default: number;
  step?: number;
  unit?: string;
}

export interface EffectInstance {
  slotId: string;
  input: AudioNode;
  output: AudioNode;
  update(params: Record<string, number>): void;
  dispose(): void;
}

export interface EffectDef {
  type: string;
  name: string;
  params: ParamDef[];
  build: (ctx: BaseAudioContext, params: Record<string, number>) => Omit<EffectInstance, 'slotId'>;
}

export function defaultParams(def: EffectDef): Record<string, number> {
  const out: Record<string, number> = {};
  for (const p of def.params) out[p.key] = p.default;
  return out;
}

/** Small helpers shared by effect builders. */
export function makeGain(ctx: BaseAudioContext, value: number): GainNode {
  const g = ctx.createGain();
  g.gain.value = value;
  return g;
}

/** Dry/wet crossfade pair: input → dry + wet → output. */
export function makeDryWet(ctx: BaseAudioContext, wet: number) {
  const input = ctx.createGain();
  const output = ctx.createGain();
  const dry = makeGain(ctx, 1 - wet);
  const wetIn = ctx.createGain();
  input.connect(dry);
  dry.connect(output);
  input.connect(wetIn);
  return { input, output, dry, wetIn, setWet: (w: number) => { dry.gain.value = 1 - w; } };
}